// src/pages/KecamatanForm.jsx

import React, { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import {
  Modal,
  TextInput,
  Button,
  LoadingOverlay,
  Alert,
  Group,
} from "@mantine/core";
import api from "../../services/api";

/**
 * Props:
 * - opened: boolean (Apakah modal terbuka?)
 * - onClose: function (Dipanggil saat modal ditutup)
 * - onSuccess: function (Dipanggil setelah simpan sukses)
 * - kecamatan: object (
 * - Saat Edit: Berisi data { id, nama_kecamatan, kota_id }
 * - Saat Tambah: Berisi { kota_id } (dari parent)
 * )
 */
export default function KecamatanForm({ opened, onClose, onSuccess, kecamatan }) {
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  
  // Tentukan mode 'Edit' (jika 'kecamatan' punya 'id')
  const isEdit = Boolean(kecamatan && kecamatan.id); 

  const form = useForm({
    initialValues: {
      nama_kecamatan: "",
      kota_id: null,
    },
    validate: {
      nama_kecamatan: (val) => (val.trim().length === 0 ? "Nama kecamatan tidak boleh kosong" : null),
      kota_id: (val) => (val === null ? "Kota ID tidak boleh kosong" : null),
    },
  });

  // Isi form saat modal dibuka
  useEffect(() => {
    if (opened) {
      if (isEdit) {
        // Mode Edit: Isi form dengan data yang ada
        form.setValues({
          nama_kecamatan: kecamatan.nama_kecamatan,
          kota_id: kecamatan.kota_id,
        });
      } else {
        // Mode Tambah: Isi 'kota_id' dari parent, reset 'nama_kecamatan'
        form.setValues({
          nama_kecamatan: "",
          kota_id: kecamatan?.kota_id, // Ambil 'kota_id' dari parent
        });
      }
    }
  }, [kecamatan, opened]); // '[]' dependency

  const handleClose = () => {
    form.reset();
    setFormError(null);
    onClose();
  };

  // Fungsi saat tombol "Simpan" (submit) ditekan
  const handleSubmit = async (values) => {
    setLoading(true);
    setFormError(null);
    
    try {
      if (isEdit) {
        // Panggil API 'updateKecamatan'
        await api.put(`/admin/kecamatan/${kecamatan.id}`, values);
      } else {
        // Panggil API 'storeKecamatan'
        await api.post("/admin/kecamatan", values);
      }
      
      onSuccess(); // Panggil callback sukses (untuk refresh tabel)
      handleClose(); // Tutup modal

    } catch (err) {
      if (err.response && err.response.status === 422) {
        setFormError(Object.values(err.response.data.errors).flat().join(", "));
      } else {
        setFormError("Gagal menyimpan data. Coba lagi.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEdit ? "Edit Kecamatan" : "Tambah Kecamatan Baru"}
      size="md"
      overlayProps={{ blur: 2 }}
    >
      <LoadingOverlay visible={loading} />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        
        {formError && (
          <Alert color="red" title="Error" withCloseButton onClose={() => setFormError(null)} mb="md">
            {formError}
          </Alert>
        )}
        
        <TextInput
          label="Nama Kecamatan"
          placeholder="Contoh: Bogor Tengah"
          {...form.getInputProps("nama_kecamatan")}
          required
        />
        
        {/* 'kota_id' disembunyikan, tapi nilainya ada di form state */}

        <Group justify="flex-end" mt="xl">
          <Button variant="default" onClick={handleClose}>
            Batal
          </Button>
          <Button type="submit" loading={loading}>
            Simpan
          </Button>
        </Group>
      </form>
    </Modal>
  );
}